import { Upload, FileText } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import RegionPicker from "@/components/RegionPicker";
import { StepHeader } from "./ProgressBar";

export default function StepUpload({ files, setFiles, text, setText, regio, setRegio }) {
  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-[fade-in-up_500ms_ease-out_both]">
      <StepHeader sub="Voeg minstens je offerte toe — als bestand of als tekst. Voeg eventueel ook algemene voorwaarden of andere documenten toe voor een vollediger beeld.">
        Upload je offerte
      </StepHeader>

      {/* Multi-file dropzone */}
      <div className="space-y-3">
        <Label className="text-[14px] font-semibold text-oc-primary">
          Bestanden — offerte + optionele bijlagen
        </Label>
        <label
          data-testid="upload-dropzone"
          htmlFor="file-input"
          className="block bg-white border-2 border-dashed border-oc-border rounded-oc p-8 text-center cursor-pointer hover:border-oc-secondary hover:bg-oc-subtle transition-colors"
        >
          <div className="w-14 h-14 rounded-full bg-oc-subtle flex items-center justify-center mx-auto">
            <Upload className="w-6 h-6 text-oc-primary" />
          </div>
          <p className="mt-4 font-display font-semibold text-oc-primary text-[16px]">
            Sleep je bestanden hierheen of klik om te selecteren
          </p>
          <p className="text-oc-textSecondary text-[13px] mt-1">
            PDF, JPG, PNG of HEIC · max 20 MB per bestand · tot 6 bestanden
          </p>
          <p className="text-oc-textSecondary text-[12px] mt-1">
            Eerste bestand = je offerte. Volgende bestanden = algemene voorwaarden of andere documentatie.
          </p>
          <input
            id="file-input"
            type="file"
            multiple
            accept=".pdf,image/jpeg,image/png,image/heic,image/heif"
            className="hidden"
            onChange={(e) => {
              const incoming = Array.from(e.target.files || []);
              setFiles((prev) => [...prev, ...incoming].slice(0, 6));
              e.target.value = "";
            }}
          />
        </label>

        {files.length > 0 && (
          <div className="space-y-2" data-testid="upload-list">
            {files.map((f, i) => (
              <div
                key={`${f.name}-${i}`}
                className="flex items-center gap-3 bg-white border border-oc-border rounded-oc-btn px-4 py-3"
              >
                <FileText className="w-5 h-5 text-oc-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold text-oc-primary truncate">
                    {i === 0 ? "Offerte: " : `Bijlage ${i}: `}
                    {f.name}
                  </p>
                  <p className="text-oc-textSecondary text-[12px]">
                    {(f.size / 1024).toFixed(0)} KB · {f.type || "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                  className="text-oc-textSecondary hover:text-oc-excessive p-2"
                  aria-label="Verwijder bestand"
                  data-testid={`upload-remove-${i}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Text input — always visible alongside files */}
      <div className="space-y-3">
        <Label className="text-[14px] font-semibold text-oc-primary">
          Of plak hier de offerte-tekst{" "}
          <span className="font-normal text-oc-textSecondary">
            (optioneel als je een bestand uploadde)
          </span>
        </Label>
        <Textarea
          data-testid="upload-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Kopieer en plak hier de teksten en prijzen uit je offerte, of voeg geschreven info van het bedrijf gerelateerd aan de offerte toe..."
          className="min-h-[180px] rounded-oc-btn text-[15px] leading-relaxed border-oc-border bg-white"
        />
        <p className="text-[12px] text-oc-textSecondary">
          Minstens 30 tekens als je geen bestand uploadt. Tekst en bestanden worden samen gebruikt voor de analyse.
        </p>
      </div>

      <RegionPicker value={regio} onChange={setRegio} testIdPrefix="region" />
    </div>
  );
}
